import { useSceneSelection } from '@gorampe/viz-3d';
import type { BOMLeaf } from '@gorampe/viz-3d';
import { useTranslation } from '@/lib/i18n';
import type { CSSProperties } from 'react';

interface Props {
  bomFlat: Map<string, BOMLeaf>;
  variant?: 'desktop' | 'mobile';
  screenPos?: { x: number; y: number } | null;
}

export function PartTooltip({ bomFlat, variant = 'desktop', screenPos }: Props) {
  const selected = useSceneSelection((s) => s.selectedPartRef);
  const { t } = useTranslation();
  if (!selected) return null;
  const leaf = bomFlat.get(selected);
  if (!leaf) return null;

  const style: CSSProperties =
    variant === 'mobile'
      ? {
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(10,17,40,0.96)',
          color: '#fff',
          padding: '16px',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          zIndex: 50,
          maxHeight: '33vh',
          overflow: 'auto',
        }
      : {
          position: 'absolute',
          left: screenPos?.x ?? 16,
          top: screenPos?.y ?? 16,
          background: 'rgba(10,17,40,0.96)',
          color: '#fff',
          padding: '10px 12px',
          borderRadius: 8,
          fontSize: 12,
          pointerEvents: 'none',
          zIndex: 40,
          minWidth: 200,
        };

  const hasRevisionOrDate = leaf.revision != null || leaf.drawingDate;

  return (
    <div role="tooltip" aria-live="polite" style={style}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {leaf.imageUrl && (
          <img
            src={leaf.imageUrl}
            alt={leaf.partNumber}
            style={{
              width: 40,
              height: 40,
              objectFit: 'contain',
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 4,
              flexShrink: 0,
            }}
            loading="lazy"
          />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'monospace' }}>
            {leaf.partNumber}
          </div>
          <div style={{ opacity: 0.85 }}>{leaf.label}</div>
        </div>
      </div>
      {leaf.dimensions && (
        <div style={{ opacity: 0.7, fontSize: 11, marginTop: 4 }}>
          {Object.entries(leaf.dimensions)
            .map(([k, v]) => `${k}: ${v}"`)
            .join(' · ')}
        </div>
      )}
      <div style={{ opacity: 0.7, fontSize: 11 }}>
        {t('bom.qty')}: {leaf.qty}
        {leaf.cutOnSite && (
          <span style={{ marginLeft: 8 }}>
            ✂ {t('bom.cut_from_stock')} {leaf.stockLengthIn}"
          </span>
        )}
      </div>
      {leaf.weightLbs != null && (
        <div style={{ opacity: 0.7, fontSize: 11 }}>
          {leaf.weightLbs.toFixed(1)} lbs
          {leaf.qty > 1 && (
            <span style={{ marginLeft: 6, opacity: 0.7 }}>
              (×{leaf.qty} = {(leaf.weightLbs * leaf.qty).toFixed(1)} lbs)
            </span>
          )}
        </div>
      )}
      <div style={{ opacity: 0.6, fontSize: 11 }}>
        {t('bom.material')}: {leaf.material ?? 'Aluminum 6005A-T61'}
      </div>
      {leaf.priceCadUnit != null && (
        <div style={{ opacity: 0.7, fontSize: 11 }}>
          {leaf.priceCadUnit.toFixed(2)} $ CAD
          {leaf.priceCadTotal != null && leaf.qty > 1 && (
            <span style={{ marginLeft: 6, opacity: 0.7 }}>
              (total {leaf.priceCadTotal.toFixed(2)} $)
            </span>
          )}
        </div>
      )}
      {hasRevisionOrDate && (
        <div style={{ opacity: 0.5, fontSize: 10, marginTop: 4, display: 'flex', gap: 8 }}>
          {leaf.revision != null && <span>R{leaf.revision}</span>}
          {leaf.drawingDate && <span>{leaf.drawingDate}</span>}
        </div>
      )}
    </div>
  );
}
